import React, { useState, useEffect } from 'react'
import { Sidebar } from '../../components/shared/Sidebar'
import { Breadcrumbs } from '../../components/shared/Breadcrumbs'
import { CreditScoreCard } from '../../components/credit/CreditScoreCard'
import { ScoreBreakdown } from '../../components/credit/ScoreBreakdown'
import { ImprovementTips } from '../../components/credit/ImprovementTips'
import { ScoreHistoryChart } from '../../components/credit/ScoreHistoryChart'
import { motion } from 'framer-motion'
import { Shield, Info, Lock, Eye, EyeOff } from 'lucide-react'
import { CreditScoringEngine } from '../../utils/creditScoring'
import {
  CreditScore,
  CreditScoreHistory,
  Recommendation,
} from '../../types/creditScore'
import { useToast } from '../../hooks/useToast'
// Mock data for demonstration
const mockCreditData = {
  bureauData: [],
  alternativeData: {
    bankStatements: [
      {
        month: '2024-01',
        averageBalance: 450000,
        inflows: 500000,
        outflows: 400000,
        bounces: 0,
        consistency: 95,
      },
      {
        month: '2024-02',
        averageBalance: 480000,
        inflows: 500000,
        outflows: 380000,
        bounces: 0,
        consistency: 95,
      },
      {
        month: '2024-03',
        averageBalance: 500000,
        inflows: 500000,
        outflows: 350000,
        bounces: 0,
        consistency: 95,
      },
    ],
    salaryData: {
      employer: 'Tech Corp Nigeria',
      monthlyIncome: 500000,
      employmentType: 'Permanent' as const,
      employmentDuration: 36,
      incomeConsistency: 95,
      lastSalaryDate: '2024-03-28',
    },
    utilityPayments: [
      {
        type: 'electricity' as const,
        provider: 'EKEDC',
        onTimePayments: 12,
        latePayments: 0,
        missedPayments: 0,
      },
      {
        type: 'internet' as const,
        provider: 'MTN',
        onTimePayments: 12,
        latePayments: 0,
        missedPayments: 0,
      },
    ],
    walletTransactions: {
      provider: 'OPay',
      monthlyInflows: 520000,
      transactionCount: 45,
      consistency: 90,
    },
  },
  platformData: {
    rentPayments: [
      { date: '2024-01-01', amount: 150000, status: 'on-time' as const },
      { date: '2024-02-01', amount: 150000, status: 'on-time' as const },
      { date: '2024-03-01', amount: 150000, status: 'on-time' as const },
    ],
    missedPayments: 0,
    latePayments: 0,
    tenancyDuration: 12,
    disputes: 0,
    supportTickets: 1,
    autopayEnabled: true,
  },
}
const mockHistory: CreditScoreHistory[] = [
  {
    date: '2024-01-01',
    generalScore: 720,
    rentScore: 750,
    riskTier: 'B',
    change: 0,
    reason: 'Initial score',
  },
  {
    date: '2024-01-15',
    generalScore: 735,
    rentScore: 765,
    riskTier: 'B',
    change: 15,
    reason: 'On-time payment',
  },
  {
    date: '2024-02-01',
    generalScore: 745,
    rentScore: 775,
    riskTier: 'B',
    change: 10,
    reason: 'Consistent income',
  },
  {
    date: '2024-02-15',
    generalScore: 755,
    rentScore: 785,
    riskTier: 'A',
    change: 10,
    reason: 'Autopay enabled',
  },
  {
    date: '2024-03-01',
    generalScore: 765,
    rentScore: 795,
    riskTier: 'A',
    change: 10,
    reason: 'Perfect payment record',
  },
]
export function CreditScore() {
  const { toast } = useToast()
  const [score, setScore] = useState<CreditScore | null>(null)
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'breakdown' | 'history' | 'improve'
  >('overview')
  useEffect(() => {
    // Calculate credit score
    const calculatedScore =
      CreditScoringEngine.calculateCreditScore(mockCreditData)
    setScore(calculatedScore)
    // Generate recommendations
    const recs = CreditScoringEngine.generateRecommendations(calculatedScore)
    setRecommendations(recs)
  }, [])
  const handleRecommendationClick = (rec: Recommendation) => {
    toast.success(`Action started: ${rec.title}`)
  }
  if (!score) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 lg:ml-[240px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Calculating your credit score...</p>
          </div>
        </main>
      </div>
    )
  }
  return (
    <div className="flex min-h-screen bg-gray-50 pb-16 lg:pb-0">
      <Sidebar />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Breadcrumbs />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                    <Shield className="h-6 w-6 text-blue-600 mr-3" />
                    Your Credit Score
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Track and improve your creditworthiness
                  </p>
                </div>
                <button
                  onClick={() => setShowSensitiveInfo(!showSensitiveInfo)}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {showSensitiveInfo ? (
                    <>
                      <EyeOff className="h-4 w-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        Hide Details
                      </span>
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 text-gray-600" />
                      <span className="text-sm text-gray-700">
                        Show Details
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Lock className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">
                    Your Data is Protected
                  </h3>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    Your credit information is encrypted and only visible to
                    you. We comply with NDPR regulations and never share your
                    data without explicit consent. Your score is calculated
                    using secure, privacy-preserving methods.
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-8 border-b border-gray-200">
              <nav className="flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'breakdown', label: 'Score Breakdown' },
                  { id: 'history', label: 'History' },
                  { id: 'improve', label: 'Improve Score' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="space-y-8">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <CreditScoreCard
                      score={score}
                      showDetails={showSensitiveInfo}
                    />
                  </div>
                  <div>
                    <ImprovementTips
                      recommendations={recommendations.slice(0, 3)}
                      onActionClick={handleRecommendationClick}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'breakdown' && <ScoreBreakdown score={score} />}

              {activeTab === 'history' && (
                <ScoreHistoryChart history={mockHistory} />
              )}

              {activeTab === 'improve' && (
                <ImprovementTips
                  recommendations={recommendations}
                  onActionClick={handleRecommendationClick}
                />
              )}
            </div>

            {/* Info Box */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    How Your Score is Used
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>
                      • <strong>Rent Approval:</strong> Landlords see your risk
                      tier (A/B/C/D) only
                    </li>
                    <li>
                      • <strong>Interest Rates:</strong> Better scores get lower
                      rates (18-35% range)
                    </li>
                    <li>
                      • <strong>Requirements:</strong> High scores may waive
                      guarantor/insurance needs
                    </li>
                    <li>
                      • <strong>Rent Limits:</strong> Your score determines
                      maximum approved rent amount
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  )
}
